// API base URL
export const API_BASE_URL = `${import.meta.env.VITE_API_URL || 'http://localhost:4000'}/api`

// API endpoints
export const API_ENDPOINTS = {
  // Auth endpoints
  AUTH: {
    LOGIN: '/auth/login',
    REGISTER: '/auth/register',
    LOGOUT: '/auth/logout',
    REFRESH_TOKEN: '/auth/refresh',
    FORGOT_PASSWORD: '/auth/forgot-password',
    RESET_PASSWORD: '/auth/reset-password',
    VERIFY_OTP: '/auth/verify-otp',
    CHANGE_PASSWORD: '/auth/change-password',
    ME: '/auth/me',
  },

  // User endpoints
  USERS: {
    BASE: '/users',
    PROFILE: '/users/profile',
    UPDATE_PROFILE: '/users/profile',
    UPLOAD_AVATAR: '/users/avatar',
  },

  // Farm endpoints
  FARMS: {
    BASE: '/farms',
    DETAIL: (id: string) => `/farms/${id}`,
    CREATE: '/farms',
    UPDATE: (id: string) => `/farms/${id}`,
    DELETE: (id: string) => `/farms/${id}`,
    STATISTICS: '/farms/statistics',
    UPLOAD_IMAGES: '/farms/images',
    COWS: (farmId: string) => `/farms/${farmId}/cows`,
  },

  // Cow endpoints
  COWS: {
    BASE: '/cows',
    DETAIL: (id: string) => `/cows/${id}`,
    CREATE: '/cows',
    UPDATE: (id: string) => `/cows/${id}`,
    DELETE: (id: string) => `/cows/${id}`,
    STATISTICS: '/cows/statistics',
    HEALTH_RECORDS: (cowId: string) => `/cows/${cowId}/health`,
    WEIGHT_RECORDS: (cowId: string) => `/cows/${cowId}/weight`,
    UPLOAD_IMAGES: '/cows/images',
    ADD_IMAGE: (cowId: string) => `/cows/${cowId}/images`,
    DELETE_IMAGE: (cowId: string, imageId: string) => `/cows/${cowId}/images/${imageId}`,
  },

  // Finance endpoints
  TRANSACTIONS: {
    BASE: '/transactions',
    DETAIL: (id: string) => `/transactions/${id}`,
    CREATE: '/transactions',
    UPDATE: (id: string) => `/transactions/${id}`,
    DELETE: (id: string) => `/transactions/${id}`,
    SUMMARY: '/transactions/summary',
    BY_FARM: (farmId: string) => `/transactions/farm/${farmId}`,
    UPLOAD_ATTACHMENTS: '/transactions/attachments',
  },

  // Budget endpoints
  BUDGETS: {
    BASE: '/budgets',
    DETAIL: (id: string) => `/budgets/${id}`,
    CREATE: '/budgets',
    UPDATE: (id: string) => `/budgets/${id}`,
    DELETE: (id: string) => `/budgets/${id}`,
  },

  // Report endpoints
  REPORTS: {
    FARM: '/reports/farm',
    COW: '/reports/cow',
    FINANCE: '/reports/finance',
    EXPORT: '/reports/export',
  },

  // Behavior endpoints
  BEHAVIORS: {
    BASE: '/behaviors',
    DETAIL: (id: string) => `/behaviors/${id}`,
    CREATE: '/behaviors',
    UPDATE: (id: string) => `/behaviors/${id}`,
    DELETE: (id: string) => `/behaviors/${id}`,
    BY_COW: (cowId: string) => `/behaviors/cow/${cowId}`,
    DAILY_SUMMARY: (cowId: string, date: string) => `/behaviors/cow/${cowId}/summary/${date}`,
    TRENDS: (cowId: string) => `/behaviors/cow/${cowId}/trends`,
  },

  // Estrus endpoints
  ESTRUS: {
    BASE: '/estrus',
    DETAIL: (id: string) => `/estrus/${id}`,
    CREATE: '/estrus',
    UPDATE: (id: string) => `/estrus/${id}`,
    DELETE: (id: string) => `/estrus/${id}`,
    BY_COW: (cowId: string) => `/estrus/cow/${cowId}`,
    BY_FARM: (farmId: string) => `/estrus/farm/${farmId}`,
    ACTIVE: '/estrus/active',
    CALENDAR: '/estrus/calendar',
  },

  // Notification endpoints
  NOTIFICATIONS: {
    BASE: '/notifications',
    DETAIL: (id: string) => `/notifications/${id}`,
    MARK_READ: (id: string) => `/notifications/${id}/read`,
    MARK_ALL_READ: '/notifications/read-all',
    DELETE: (id: string) => `/notifications/${id}`,
    STATISTICS: '/notifications/statistics',
    PREFERENCES: '/notifications/preferences',
    UPDATE_PREFERENCES: '/notifications/preferences',
  },

  // Alert rules endpoints
  ALERT_RULES: {
    BASE: '/alert-rules',
    DETAIL: (id: string) => `/alert-rules/${id}`,
    CREATE: '/alert-rules',
    UPDATE: (id: string) => `/alert-rules/${id}`,
    DELETE: (id: string) => `/alert-rules/${id}`,
    TOGGLE: (id: string) => `/alert-rules/${id}/toggle`,
    BY_FARM: (farmId: string) => `/alert-rules/farm/${farmId}`,
  },

  // Farmer profile endpoints
  FARMERS: {
    PROFILE: '/farmers/profile',
    UPDATE_PROFILE: '/farmers/profile',
    DASHBOARD: '/farmers/dashboard',
    STATISTICS: '/farmers/statistics',
    DASHBOARD_PREFERENCES: '/farmers/dashboard-preferences',
  },

  // Heat alert endpoints
  HEAT_ALERTS: {
    BASE: '/heat-alerts',
    BY_COW: (cowId: string) => `/heat-alerts/cow/${cowId}`,
    CHECK: (cowId: string) => `/heat-alerts/check/${cowId}`,
  },

  // Health & Weight endpoints
  HEALTH: {
    WEIGHT_RECORDS: '/health/weights',
    WEIGHT_BY_COW: (cowId: string) => `/health/weight/${cowId}`,
    CREATE_WEIGHT: '/health/weight',
    BULK_CREATE_WEIGHTS: '/health/weights/bulk',
  },

  // Feeding endpoints
  FEEDING: {
    BASE: '/feeding',
    DETAIL: (id: string) => `/feeding/${id}`,
    CREATE: '/feeding',
    UPDATE: (id: string) => `/feeding/${id}`,
    DELETE: (id: string) => `/feeding/${id}`,
    STATISTICS: '/feeding/statistics',
  },
} as const

// API request timeout
export const API_TIMEOUT = 30000 // 30 seconds

// API headers
export const API_HEADERS = {
  'Content-Type': 'application/json',
  Accept: 'application/json',
}
